import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2, ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/layout/AppShell";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthUser } from "@/context/AuthContext";
import { ApiError } from "@/lib/api";
import {
  subscriptionsKeys,
  useCheckoutSubscription,
  usePaymentStatusPolling,
  useSubscriptions,
} from "@/lib/api/subscriptions.queries";
import type {
  SubscriptionPeriod,
  SubscriptionRow,
  SubscriptionState,
} from "@/lib/api/subscriptions";
import { useActiveTeam } from "@/lib/team-context";

/**
 * Strip the +57 country code from a stored phone (E.164-ish) so the input
 * shows just the 10 local digits. Mirrors pahcertificar's NequiPhoneDialog.
 */
function toLocalPhone(phone: string | null | undefined): string {
  const digits = (phone ?? "").replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("57")) return digits.slice(2);
  if (digits.length === 13 && digits.startsWith("057")) return digits.slice(3);
  return digits.slice(-10);
}

export const Route = createFileRoute("/subscription/")({
  component: SubscriptionPage,
});

const PRICE_BY_PERIOD: Record<SubscriptionPeriod, number> = {
  monthly: 49_000,
  annual: 499_000,
};

const SAVINGS_PCT = Math.round(
  (1 - PRICE_BY_PERIOD.annual / (PRICE_BY_PERIOD.monthly * 12)) * 100,
);



function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function stateBadgeVariant(
  state: SubscriptionState,
): "default" | "secondary" | "destructive" | "outline" {
  switch (state) {
    case "active":
      return "default";
    case "trial":
      return "secondary";
    case "pending":
      return "outline";
    case "past_due":
    case "trial_expired":
    case "cancelled":
      return "destructive";
    default:
      return "outline";
  }
}

/**
 * Five steps the dialog cycles through for a subscribe attempt:
 *
 *   form     → show period + phone, button is "Pagar con Nequi".
 *   waiting  → checkout returned, polling /api/me/payments/{token} every 3s.
 *              UI shows a spinner + elapsed time + "approve in Nequi".
 *   success  → intent.status === "succeeded" — show check + close button.
 *   error    → terminal failure (Nequi rejected, payment misconfigured,
 *              user cancelled, timeout). Show retry button → form.
 */
type DialogStep = "form" | "waiting" | "success" | "error";

const POLL_TIMEOUT_MS = 3 * 60 * 1000; // 3 min — Nequi push expires server-side anyway
const POLL_INTERVAL_MS = 3000;

function SubscriptionPage() {
  const formatCop = useMoneyFormatter();
  const { t } = useTranslation();
  const me = useAuthUser();
  const queryClient = useQueryClient();
  const { activeTeamId } = useActiveTeam();
  const subsQuery = useSubscriptions();
  const checkout = useCheckoutSubscription();
  const [openFor, setOpenFor] = useState<SubscriptionRow | null>(null);
  const [period, setPeriod] = useState<SubscriptionPeriod>("annual");
  const [phone, setPhone] = useState<string>(() => toLocalPhone(me.phone));
  const [phoneError, setPhoneError] = useState<{
    code: string;
    message: string;
  } | null>(null);
  const [step, setStep] = useState<DialogStep>("form");
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [terminalError, setTerminalError] = useState<string | null>(null);
  const [waitStartedAt, setWaitStartedAt] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);

  // Live status while waiting for Nequi approval.
  const statusQuery = usePaymentStatusPolling(pendingToken, {
    enabled: step === "waiting" && !!pendingToken,
    intervalMs: POLL_INTERVAL_MS,
  });

  // Reset state every time the dialog opens fresh.
  useEffect(() => {
    if (openFor) {
      setPhone(toLocalPhone(me.phone));
      setPhoneError(null);
      setStep("form");
      setPendingToken(null);
      setTerminalError(null);
      setWaitStartedAt(null);
      setElapsed(0);
    }
  }, [openFor, me.phone]);

  // Tick once a second while waiting, just to render the elapsed counter.
  useEffect(() => {
    if (step !== "waiting" || waitStartedAt === null) return;
    const id = window.setInterval(() => {
      setElapsed(Date.now() - waitStartedAt);
    }, 1000);
    return () => window.clearInterval(id);
  }, [step, waitStartedAt]);

  // React to status transitions while waiting.
  useEffect(() => {
    if (step !== "waiting") return;
    const intentStatus = statusQuery.data?.intent?.status;
    if (!intentStatus) return;
    if (intentStatus === "succeeded") {
      // Bridge from payment worker has fired by now (the GET status endpoint
      // self-reconciles). Refetch the subscription list so the row flips
      // visually from `pending` → `active`.
      queryClient.invalidateQueries({
        queryKey: subscriptionsKeys.list(activeTeamId),
      });
      if (openFor) {
        queryClient.invalidateQueries({
          queryKey: subscriptionsKeys.detail(activeTeamId, openFor.companyId),
        });
      }
      setStep("success");
      return;
    }
    if (intentStatus === "failed" || intentStatus === "cancelled") {
      setTerminalError(
        intentStatus === "cancelled"
          ? t("subscription.waiting.cancelled")
          : t("subscription.waiting.failed"),
      );
      setStep("error");
    }
  }, [
    step,
    statusQuery.data,
    queryClient,
    activeTeamId,
    openFor,
    t,
  ]);

  // Timeout: stop waiting after POLL_TIMEOUT_MS so the dialog doesn't hang
  // forever if the user closes Nequi without approving.
  useEffect(() => {
    if (step !== "waiting" || waitStartedAt === null) return;
    const id = window.setTimeout(() => {
      setTerminalError(t("subscription.waiting.timeout"));
      setStep("error");
    }, POLL_TIMEOUT_MS);
    return () => window.clearTimeout(id);
  }, [step, waitStartedAt, t]);

  const isLoading = subsQuery.isPending;
  const isError = subsQuery.isError;
  const data = subsQuery.data;
  const items = data?.items ?? [];
  const teamRole = data?.teamRole;
  const canSubscribeRole = teamRole === "owner" || teamRole === "admin";
  const phoneValid = /^\d{10}$/.test(phone);

  const onSubscribeClick = (row: SubscriptionRow) => {
    setPeriod(row.subscription.period ?? "annual");
    setOpenFor(row);
  };

  const onConfirm = async () => {
    if (!openFor || !phoneValid) return;
    setPhoneError(null);
    try {
      const res = await checkout.mutateAsync({
        companyId: openFor.companyId,
        period,
        nequiPhone: `+57${phone}`,
      });
      // Stay open. Transition to waiting and start polling.
      setPendingToken(res.token);
      setWaitStartedAt(Date.now());
      setElapsed(0);
      setStep("waiting");
      // Some flows return a redirect URL (none today for Nequi push, but
      // keep the support — opens in a new tab so the user can finish there
      // while we keep polling here).
      if (res.checkoutUrl) {
        window.open(res.checkoutUrl, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      // Nequi-side rejections (NEQUI_PAYER_NOT_FOUND, NEQUI_INVALID_PHONE,
      // ...) → keep dialog on form, show inline so the user can fix the phone.
      if (err instanceof ApiError && err.code?.startsWith("NEQUI_")) {
        setPhoneError({ code: err.code, message: err.message });
        return;
      }
      // Misconfiguration / network / 5xx → toast + close.
      toast.error(err instanceof Error ? err.message : String(err));
      setOpenFor(null);
    }
  };

  const onRetry = () => {
    setStep("form");
    setPendingToken(null);
    setWaitStartedAt(null);
    setElapsed(0);
    setTerminalError(null);
    setPhoneError(null);
  };

  const onCloseDialog = () => {
    // Don't allow closing mid-checkout. A succeeded or errored dialog
    // can be closed; a waiting one only via Cancelar (which ends polling
    // and bumps to error).
    setOpenFor(null);
  };

  return (
    <RequireAuth>
      <AppShell>
        <div className="mx-auto max-w-5xl space-y-6">
          <div>
            <h1 className="text-2xl font-semibold">{t("subscription.title")}</h1>
            <p className="text-sm text-muted-foreground">
              {t("subscription.description")}
            </p>
          </div>

          {!canSubscribeRole && data && (
            <Alert variant="info">
              <AlertTitle>{t("subscription.permissions.title")}</AlertTitle>
              <AlertDescription>
                {t("subscription.permissions.read_only")}
              </AlertDescription>
            </Alert>
          )}

          {isLoading && (
            <Card>
              <CardContent className="space-y-3 p-6">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-72" />
                <Skeleton className="h-10 w-32" />
              </CardContent>
            </Card>
          )}

          {isError && (
            <Alert variant="destructive">
              <AlertTitle>{t("common.error")}</AlertTitle>
              <AlertDescription>
                {subsQuery.error instanceof Error
                  ? subsQuery.error.message
                  : t("subscription.list.load_failed")}
              </AlertDescription>
            </Alert>
          )}

          {!isLoading && !isError && items.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center text-sm text-muted-foreground">
                {t("subscription.empty")}
              </CardContent>
            </Card>
          )}

          {items.map((row) => (
            <CompanySubscriptionCard
              key={row.companyId}
              row={row}
              canSubscribe={row.canSubscribe}
              onSubscribe={() => onSubscribeClick(row)}
            />
          ))}
        </div>

        <Dialog
          open={openFor !== null}
          onOpenChange={(open) => {
            if (open) return;
            // While waiting we don't allow closing — the user must click
            // "Cancelar" which routes through the error step.
            if (step === "waiting") return;
            onCloseDialog();
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {step === "success"
                  ? t("subscription.success.title")
                  : step === "error"
                    ? t("subscription.error.title")
                    : step === "waiting"
                      ? t("subscription.waiting.title")
                      : openFor?.subscription.state === "active"
                        ? t("subscription.dialog.title.renew")
                        : t("subscription.dialog.title.subscribe")}
              </DialogTitle>
              <DialogDescription>
                {step === "form" && openFor
                  ? t("subscription.dialog.description", {
                      company: openFor.legalName,
                    })
                  : step === "waiting"
                    ? t("subscription.waiting.subtitle")
                    : step === "success"
                      ? t("subscription.success.subtitle", {
                          company: openFor?.legalName ?? "",
                        })
                      : step === "error"
                        ? t("subscription.error.subtitle")
                        : ""}
              </DialogDescription>
            </DialogHeader>

            {step === "form" && (
              <div className="space-y-4">
                <Tabs
                  value={period}
                  onValueChange={(v) => setPeriod(v as SubscriptionPeriod)}
                >
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="annual">
                      {t("subscription.period.annual")}{" "}
                      <span className="ml-2 text-xs text-emerald-600 dark:text-emerald-400">
                        {t("subscription.price.annual_savings", { pct: SAVINGS_PCT })}
                      </span>
                    </TabsTrigger>
                    <TabsTrigger value="monthly">
                      {t("subscription.period.monthly")}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                <div className="rounded-md border bg-muted/30 p-4 text-center">
                  <p className="text-3xl font-semibold">
                    {formatCop(PRICE_BY_PERIOD[period])}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {period === "annual"
                      ? t("subscription.price.annual_unit")
                      : t("subscription.price.monthly_unit")}
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="nequi-phone">
                    {t("subscription.nequi.phone_label")}
                  </Label>
                  <div className="flex items-stretch overflow-hidden rounded-md border focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                    <span className="flex items-center border-r bg-muted/50 px-3 text-sm font-medium text-muted-foreground">
                      🇨🇴 +57
                    </span>
                    <Input
                      id="nequi-phone"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel-national"
                      maxLength={10}
                      placeholder="3001234567"
                      value={phone}
                      onChange={(e) => {
                        setPhone(e.target.value.replace(/\D/g, "").slice(0, 10));
                        if (phoneError) setPhoneError(null);
                      }}
                      disabled={checkout.isPending}
                      aria-invalid={phoneError ? "true" : undefined}
                      className="flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t("subscription.nequi.phone_helper")}
                  </p>
                  {phoneError && (
                    <p className="text-xs text-destructive">
                      {phoneError.code === "NEQUI_PAYER_NOT_FOUND"
                        ? t("subscription.nequi.err_not_found")
                        : phoneError.message}
                    </p>
                  )}
                </div>

                <p className="text-xs text-muted-foreground">
                  {t("subscription.dialog.disclaimer_nequi")}
                </p>
              </div>
            )}

            {step === "waiting" && (
              <div className="flex flex-col items-center gap-4 py-6 text-center">
                <div className="relative inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                  <Loader2 className="h-7 w-7 animate-spin text-primary" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium">
                    {t("subscription.waiting.body", {
                      phone: `+57 ${phone}`,
                    })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t("subscription.waiting.hint")}
                  </p>
                </div>
                <p className="font-mono text-xs text-muted-foreground">
                  {t("subscription.waiting.elapsed", {
                    seconds: Math.floor(elapsed / 1000),
                  })}
                </p>
              </div>
            )}

            {step === "success" && (
              <div className="flex flex-col items-center gap-4 py-6 text-center">
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <p className="text-sm">
                  {t("subscription.success.body", {
                    amount: formatCop(PRICE_BY_PERIOD[period]),
                    company: openFor?.legalName ?? "",
                  })}
                </p>
              </div>
            )}

            {step === "error" && (
              <div className="space-y-3">
                <Alert variant="destructive">
                  <ShieldAlert className="h-4 w-4" />
                  <AlertTitle>{t("subscription.error.alert_title")}</AlertTitle>
                  <AlertDescription>
                    {terminalError ?? t("subscription.error.fallback")}
                  </AlertDescription>
                </Alert>
              </div>
            )}

            <DialogFooter>
              {step === "form" && (
                <>
                  <Button
                    variant="outline"
                    onClick={onCloseDialog}
                    disabled={checkout.isPending}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    onClick={onConfirm}
                    disabled={checkout.isPending || !phoneValid}
                  >
                    {checkout.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    {t("subscription.dialog.confirm")}
                  </Button>
                </>
              )}
              {step === "waiting" && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setTerminalError(t("subscription.waiting.cancelled"));
                    setStep("error");
                  }}
                >
                  {t("subscription.waiting.cancel")}
                </Button>
              )}
              {step === "success" && (
                <Button onClick={onCloseDialog}>
                  {t("subscription.success.close")}
                </Button>
              )}
              {step === "error" && (
                <>
                  <Button variant="outline" onClick={onCloseDialog}>
                    {t("common.close")}
                  </Button>
                  <Button onClick={onRetry}>
                    {t("subscription.error.retry")}
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </AppShell>
    </RequireAuth>
  );
}

function CompanySubscriptionCard({
  row,
  canSubscribe,
  onSubscribe,
}: {
  row: SubscriptionRow;
  canSubscribe: boolean;
  onSubscribe: () => void;
}) {
  const formatCop = useMoneyFormatter();
  const { t } = useTranslation();
  const { subscription: sub, legalName, nit } = row;
  const stateLabel = t(`subscription.state.${sub.state}`);
  const ctaKey =
    sub.state === "active" || sub.state === "pending"
      ? "subscription.button.renew"
      : "subscription.button.subscribe";

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-lg">{legalName}</CardTitle>
          <CardDescription>NIT {nit}</CardDescription>
        </div>
        <Badge variant={stateBadgeVariant(sub.state)}>{stateLabel}</Badge>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
          <div>
            <p className="text-xs uppercase text-muted-foreground">
              {t("subscription.field.period")}
            </p>
            <p>
              {sub.period
                ? t(`subscription.period.${sub.period}`)
                : t("common.dash")}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase text-muted-foreground">
              {t("subscription.field.amount")}
            </p>
            <p>{sub.amountCop ? formatCop(sub.amountCop) : t("common.dash")}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-muted-foreground">
              {t("subscription.field.renewal")}
            </p>
            <p>
              {sub.state === "trial"
                ? t("subscription.trial.endsAt", {
                    date: formatDate(sub.trialEndsAt),
                  })
                : formatDate(sub.currentPeriodEnd)}
            </p>
          </div>
        </div>

        {sub.state === "trial" && (
          <Alert variant="info">
            <AlertTitle>{t("subscription.trial.banner_title")}</AlertTitle>
            <AlertDescription>
              {t("subscription.trial.banner_body", {
                date: formatDate(sub.trialEndsAt),
              })}
            </AlertDescription>
          </Alert>
        )}

        {sub.state === "trial_expired" && (
          <Alert variant="destructive">
            <AlertTitle>{t("subscription.trial_expired.banner_title")}</AlertTitle>
            <AlertDescription>
              {t("subscription.trial_expired.banner_body")}
            </AlertDescription>
          </Alert>
        )}

        {sub.state === "past_due" && (
          <Alert variant="destructive">
            <AlertTitle>{t("subscription.past_due.banner_title")}</AlertTitle>
            <AlertDescription>
              {t("subscription.past_due.banner_body")}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex items-center justify-end">
          <Button onClick={onSubscribe} disabled={!canSubscribe}>
            {t(ctaKey)}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
